import { useEffect, useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import {
  usePersonalInformation,
  useUpdateUserProfile,
} from '@scfhp-web/shared-module';
import {
  trackAnalyticsEvent,
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
} from '@leagueplatform/analytics';
import { COMMUNICATION_OPTIONS } from 'commons/constants';

export const useCommunicationPreferences = () => {
  const { formatMessage } = useIntl();
  const { updateUserProfile } = useUpdateUserProfile({
    onSuccess: () => {},
    onError: () => {},
  });
  const { data: personalInformation, isLoading } = usePersonalInformation();

  const [allowEmailNotification, setAllowEmailNotification] = useState<
    undefined | boolean
  >();
  const [isWaiting, setIsWaiting] = useState('');

  const isEmailNotificationEnabled =
    !!personalInformation?.userProfile?.optedIntoMarketingCommunications;

  useEffect(() => {
    if (isLoading === false && allowEmailNotification === undefined) {
      setAllowEmailNotification(isEmailNotificationEnabled);
    }
  }, [
    allowEmailNotification,
    personalInformation,
    isEmailNotificationEnabled,
    isLoading,
  ]);

  const setEmailNotification = async () => {
    if (isWaiting) return;

    setIsWaiting(COMMUNICATION_OPTIONS.EMAIL);
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.COMMUNICATION_PREFERENCES,
      detail: 'user clicks the push notification toggle ',
      email_status: allowEmailNotification ? 'off' : 'on',
    });

    const optedIntoMarketingCommunications = !allowEmailNotification;

    try {
      setAllowEmailNotification((prev) => !prev);
      await updateUserProfile({
        optedIntoMarketingCommunications,
      });
    } catch (err) {
      setAllowEmailNotification((prev) => !prev);
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: formatMessage({ id: 'STR_EMAIL_NOTIFICATION_PREFERENCE_ERR' }),
      });
    }
    setIsWaiting('');
  };

  return [
    {
      allowEmailNotification,
      isLoading,
      isWaiting,
      email: personalInformation?.email,
      showEmail:
        !!allowEmailNotification && !(isLoading || isWaiting === 'email'),
    },
    { setEmailNotification },
  ];
};
